/**
 * Offline request queue for CSA — keeps critical mutations (nozzle reading
 * saves) durable across network drops. Uses IndexedDB (via a thin custom
 * wrapper — no npm dep) so the queue survives app close / phone restart.
 *
 * Public API:
 *   isOnline()              → bool
 *   enqueue({url, method, body, meta}) → id
 *   pending()               → array of pending items
 *   drain(runFn)            → replays every queued item via runFn(item);
 *                             removes on success, keeps on failure.
 *   subscribe(cb)           → listener called after every queue mutation
 *
 * We deliberately keep this in localStorage-JSON form for max compatibility
 * — the queue only holds tiny payloads (numeric readings) so no size worry.
 */
const KEY = "hisabx.offlineQueue.v1";
const listeners = new Set();

function readAll() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function writeAll(items) {
  try { localStorage.setItem(KEY, JSON.stringify(items)); } catch { /* full storage */ }
  listeners.forEach((cb) => { try { cb(items); } catch { /* consumer failure */ } });
}

export function isOnline() {
  return typeof navigator === "undefined" ? true : navigator.onLine !== false;
}

export function enqueue({ url, method = "POST", body, meta }) {
  const id = `oq-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const items = readAll();
  items.push({ id, url, method, body, meta, queued_at: new Date().toISOString(), tries: 0 });
  writeAll(items);
  return id;
}

export function pending() {
  return readAll();
}

export function pendingCount() {
  return readAll().length;
}

export function subscribe(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/**
 * Replay every queued item. `runFn(item)` should return a Promise that
 * resolves on success and rejects on any failure. Successful items are
 * removed. Failed items stay so we can retry next time.
 */
export async function drain(runFn) {
  if (!isOnline()) return { ok: 0, fail: 0, skipped: true };
  let items = readAll();
  if (items.length === 0) return { ok: 0, fail: 0 };
  const remaining = [];
  let okCount = 0;
  let failCount = 0;
  for (const it of items) {
    try {
      await runFn(it);
      okCount += 1;
    } catch {
      failCount += 1;
      remaining.push({ ...it, tries: (it.tries || 0) + 1 });
    }
  }
  writeAll(remaining);
  return { ok: okCount, fail: failCount };
}

/**
 * Wire browser online/offline events → auto-drain via the provided runner.
 * Returns an unsubscribe function.
 */
export function autoDrain(runFn) {
  const handler = () => { drain(runFn).catch(() => {}); };
  window.addEventListener("online", handler);
  // Also drain on mount in case we were offline when the app loaded.
  if (isOnline()) handler();
  return () => window.removeEventListener("online", handler);
}
